<script setup lang="ts">
import { ref } from 'vue'

interface Toast {
  id: number
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
}

const toasts = ref<Toast[]>([])
let nextId = 0

function show(message: string, type: Toast['type'] = 'info', duration = 3000) {
  const id = nextId++
  toasts.value.push({ id, message, type })

  setTimeout(() => {
    remove(id)
  }, duration)
}

function remove(id: number) {
  const index = toasts.value.findIndex(t => t.id === id)
  if (index >= 0) {
    toasts.value.splice(index, 1)
  }
}

// Expose show method for parent components
defineExpose({ show })
</script>

<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="toast.type"
          @click="remove(toast.id)"
        >
          <span class="toast-icon">
            <template v-if="toast.type === 'success'">&#10003;</template>
            <template v-else-if="toast.type === 'error'">&#10005;</template>
            <template v-else-if="toast.type === 'warning'">&#9888;</template>
            <template v-else>&#9432;</template>
          </span>
          <span class="toast-message">{{ toast.message }}</span>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toast {
  background: linear-gradient(135deg, #1a1a2e 0%, #0d0d1a 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  min-width: 250px;
  max-width: 400px;
}

.toast.success {
  border-color: rgba(39, 174, 96, 0.5);
}

.toast.success .toast-icon {
  color: #27ae60;
}

.toast.error {
  border-color: rgba(255, 68, 102, 0.5);
}

.toast.error .toast-icon {
  color: #ff4466;
}

.toast.warning {
  border-color: rgba(243, 156, 18, 0.5);
}

.toast.warning .toast-icon {
  color: #f39c12;
}

.toast.info {
  border-color: rgba(0, 212, 255, 0.5);
}

.toast.info .toast-icon {
  color: #00d4ff;
}

.toast-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.toast-message {
  color: #e0e0e0;
  font-size: 14px;
}

/* Transition */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
